import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { trackAddToCart, trackRemoveFromCart } from '../utils/analytics';

// Cart Context
const CartContext = createContext();

// Cart reducer
const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => String(item.id) === String(action.payload.id));
      if (existingItem) {
        // If item already exists, increase quantity
        return {
          ...state,
          items: state.items.map(item =>
            String(item.id) === String(action.payload.id)
              ? { ...item, quantity: item.quantity + (action.payload.quantity || 1) }
              : item
          ),
        };
      }
      // If item doesn't exist, add it with quantity (default to 1 if not specified)
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: action.payload.quantity || 1 }],
      };

    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => String(item.id) !== String(action.payload)),
      };

    case 'UPDATE_QUANTITY':
      if (action.payload.quantity <= 0) {
        return {
          ...state,
          items: state.items.filter(item => String(item.id) !== String(action.payload.id)),
        };
      }
      return {
        ...state,
        items: state.items.map(item =>
          String(item.id) === String(action.payload.id)
            ? { ...item, quantity: action.payload.quantity }
            : item
        ),
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: [],
      };

    default:
      return state;
  }
};

// Cart Provider component
export const CartProvider = ({ children }) => {
  // Initialize state with localStorage data
  const getInitialState = () => {
    const savedCart = localStorage.getItem('vanbelle-cart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        return { items: cartData.items || [] };
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        return { items: [] };
      }
    }
    return { items: [] };
  };

  const [state, dispatch] = useReducer(cartReducer, getInitialState());

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('vanbelle-cart', JSON.stringify(state));
  }, [state]);

  // Cart actions
  // `product.quantity` adds several at once (default 1)
  const addToCart = (product) => {
    dispatch({ type: 'ADD_TO_CART', payload: product });
    trackAddToCart(product, product.quantity || 1);
  };

  const removeFromCart = (productId) => {
    const item = state.items.find(i => String(i.id) === String(productId));
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
    if (item) trackRemoveFromCart(item);
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  // Calculate total price
  const getTotalPrice = () => {
    return state.items.reduce((total, item) => {
      const price = typeof item.price === 'string'
        ? parseFloat(item.price.replace('$', ''))
        : parseFloat(item.price);
      return total + (price * item.quantity);
    }, 0);
  };

  // Get total items count
  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    items: state.items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};